// Cross-component bridge to show the global AuthRequiredModal. Mirrors
// insufficientBalance.js's window-event bridge — producers (http_service's
// 401/403 hooks, the jQuery ajaxError bootstrap) aren't inside the modal's
// React tree, so they notify it via a window CustomEvent instead of prop
// drilling.

export const AUTH_REQUIRED_EVENT = "aidea:auth-required";

// Guard against dispatch storms: once shown, stays shown until reload — no
// need to re-dispatch on every subsequent 401/403.
let triggered = false;

// Session recovery hook, registered by environment_config (which owns the
// refresh token — importing it here would be circular). A plain auth failure
// (no `reason`) first tries it: an expired access token is renewed silently
// and the modal never shows. Resolves falsy only when the session is really
// over (refresh token rejected or missing); a truthy value — renewed, a stale
// 401, or "transient" (mw unreachable, retried in the background) — means
// signing in again would not help, so no modal.
let recoveryHandler = null;
// One recovery at a time: the burst of 401s an expired token produces (plus
// the global ajaxError hook firing for the same response) all await it.
let recoveryInFlight = null;

export function setAuthRecoveryHandler(handler) {
  recoveryHandler = handler;
}

function recoverAuth() {
  if (!recoveryInFlight) {
    recoveryInFlight = Promise.resolve()
      .then(() => recoveryHandler())
      .catch(() => false)
      .then((recovered) => {
        recoveryInFlight = null;
        return recovered;
      });
  }
  return recoveryInFlight;
}

function dispatchAuthRequired(reason) {
  if (triggered) return;
  triggered = true;
  try {
    window.dispatchEvent(new CustomEvent(AUTH_REQUIRED_EVENT, { detail: { reason } }));
  } catch (err) {
    console.warn("[authRequired] dispatch failed", err);
  }
}

// Resolves with the recovery result so a caller can re-send its request:
// `true` means a live token is in place now. Fire-and-forget callers may
// ignore it.
export function triggerAuthRequired(reason) {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (triggered) return Promise.resolve(false);
  if (!reason && recoveryHandler) {
    return recoverAuth().then((recovered) => {
      if (!recovered) dispatchAuthRequired(reason);
      return recovered;
    });
  }
  dispatchAuthRequired(reason);
  return Promise.resolve(false);
}

// Second latch: first-load vs runtime. No existing Redux flag means "the
// initial training load finished" — set once the editor has a training
// state to show (or determined it doesn't need one), so later failures can
// be classified as runtime rather than first-load.
let editorLoaded = false;

export function markEditorLoaded() {
  editorLoaded = true;
}

export function isEditorLoaded() {
  return editorLoaded;
}

export function isAuthRequiredTriggered() {
  return triggered;
}

// Clears the latch so a later real 401/403 can re-show the modal after the
// user dismisses a spurious one.
export function resetAuthRequired() {
  triggered = false;
}
